import { AfterViewInit, Directive, ElementRef, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { PhxBreakpointObserverService } from '../../services/phx-breakpoint-service';

const RECALC_DEBOUNCE_MS = 100;

/**
 * Dynamically sets the phx-table host height to fill the remaining viewport
 * after subtracting external chrome (filter row, page header, dynamic banners, etc.).
 *
 * The internal flexbox layout of phx-table (scroll-container + pagination) handles
 * the rest — only the host height needs to be set.
 *
 * Usage:
 * ```html
 * <phx-table phxDynamicHeight ...></phx-table>
 * ```
 */
@Directive({
  selector: '[phxDynamicHeight]',
})
export class PhxTableDynamicHeightDirective implements AfterViewInit, OnDestroy {

  private dynamicElementsMutationObserver: MutationObserver | null = null;
  private recalcTimer: ReturnType<typeof setTimeout> | null = null;
  private breakpointObserverSubscription: Subscription | null = null;

  constructor(private hostRef: ElementRef<HTMLElement>, private phxBreakPointObserver: PhxBreakpointObserverService) {}

  ngAfterViewInit(): void {
    this.setupObservers();
    // Defer initial calc so the DOM has settled.
    requestAnimationFrame(() => this.calculateHeight());
  }

  ngOnDestroy(): void {
    this.teardownObservers();
  }

  // ── Core calculation ──────────────────────────

  private calculateHeight(): void {
    const filterRow = document.querySelector('phx-filter-row') as HTMLElement | null;
    const pageHeader = document.querySelector('page-header') as HTMLElement | null;
    const tableSelectionStatus = document.querySelector('.table-selection-status') as HTMLElement | null;
    const dynamicElements = document.querySelectorAll('.dynamic-elements') as NodeListOf<HTMLElement>;

    const filterRowHeight = filterRow?.getBoundingClientRect().height ?? 0;
    const pageHeaderHeight = pageHeader?.getBoundingClientRect().height ?? 0;
    const tableSelectionStatusHeight = tableSelectionStatus?.getBoundingClientRect().height ?? 0;
    const dynamicElementsHeight = Array.from(dynamicElements)
      .reduce((sum, el) => sum + el.getBoundingClientRect().height, 0);

    let totalOffset = filterRowHeight + pageHeaderHeight + dynamicElementsHeight;

    // If the filter row isn't rendered, the UI may show a selection status bar instead.
    // Only count it when filter row is absent to avoid double-counting.
    if (!filterRowHeight && tableSelectionStatusHeight) {
      totalOffset += tableSelectionStatusHeight;
    }

    this.hostRef.nativeElement.style.height = `calc(100vh - ${totalOffset}px)`;
  }

  private scheduleRecalc(): void {
    if (this.recalcTimer != null) {
      clearTimeout(this.recalcTimer);
    }
    this.recalcTimer = setTimeout(() => this.calculateHeight(), RECALC_DEBOUNCE_MS);
  }

  // ── Observers ─────────────────────────────────

  private setupObservers(): void {

    // 1. Breakpoint changes (handles height changes on smaller viewports)
    this.breakpointObserverSubscription = this.phxBreakPointObserver.smMobile.subscribe(() => {
      // Calculate height when mobile breakpoint is entered/exited, since the filter row may be hidden/shown.
      this.scheduleRecalc();
    });

    // 2. Dynamic elements (banners, alerts added/removed)
    const mutationRoot = document.querySelector('.pusher') as HTMLElement | null;
    if (mutationRoot) {
      this.dynamicElementsMutationObserver = new MutationObserver((mutations) => {
        const relevant = mutations.some((m) =>
          this.hasMatchingNode(m.addedNodes, '.dynamic-elements')
          || this.hasMatchingNode(m.removedNodes, '.dynamic-elements')
        );
        if (relevant) {
          this.scheduleRecalc();
        }
      });
      this.dynamicElementsMutationObserver.observe(mutationRoot, {
        childList: true,
        subtree: true,
      });
    }
  }

  private teardownObservers(): void {
    this.dynamicElementsMutationObserver?.disconnect();
    this.dynamicElementsMutationObserver = null;

    if (this.recalcTimer != null) {
      clearTimeout(this.recalcTimer);
      this.recalcTimer = null;
    }
    this.breakpointObserverSubscription?.unsubscribe();
    this.breakpointObserverSubscription = null;
  }

  private hasMatchingNode(nodes: NodeList, selector: string): boolean {
    return Array.from(nodes).some((node) => {
      if (!(node instanceof Element)) return false;
      return node.matches(selector) || !!node.querySelector(selector);
    });
  }
}
